import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { observer } from 'mobx-react-lite';
import { useFieldActions } from './hooks/useFieldActions';
import { useFieldPrompts } from './hooks/useFieldPrompts';
import { useFieldSelection } from './hooks/useFieldSelection';
import { createStyles } from '@mantine/emotion';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  Box,
  Text,
  Flex,
  Checkbox,
  Button,
  ActionIcon,
  Menu,
  Input,
  Tooltip,
  UnstyledButton,
  CloseButton
} from '@mantine/core';
import extractionStore from '../../../store';
import {
  computeCustomFields,
  getRequiredFields,
  shouldShowRemapButton,
  isFieldSelected
} from './utils/fieldUtils';
import { BiChevronDown } from 'react-icons/bi';
import {
  TbCheck,
  TbTrash,
  TbTypography,
  TbCalendar,
  TbPencil,
  TbToggleLeft,
  Tb123
} from 'react-icons/tb';
import { GoNumber } from 'react-icons/go';
import ConfirmModal from '../../../../../components/ConfirmModal';
import { variableTypes } from '../../../../../consts/variableTypes';
import { REPORT_TYPES } from '../../../../../consts/reportTypes';
import FieldMenu from './components/FieldMenu';
import { idCreator } from '../../../../Project/utils/idCreator';

export const CUSTOM_FIELD_TYPE_OPTIONS = [
  { type: variableTypes.STRING, label: 'Text', Icon: TbTypography },
  { type: variableTypes.NUMBER, label: 'Number', Icon: GoNumber },
  { type: variableTypes.INTEGER, label: 'Integer', Icon: Tb123 },
  { type: variableTypes.BOOLEAN, label: 'Boolean', Icon: TbToggleLeft },
  { type: variableTypes.DATE_TIME, label: 'Date', Icon: TbCalendar }
];

const MenuTableItem = ({ idPrefix, onSelectType, onSelectTable, table }) => {
  const handleTableClick = () => {
    onSelectTable(table);
  };
  const rootId = idPrefix || `grid-table-fields-menu-table-${idCreator(table)}`;

  return (
    <Menu id={rootId} shadow="md" width={200} position="right-end">
      <Menu.Target>
        <UnstyledButton id={`${rootId}-target`}>
          <Menu.Item id={`${rootId}-table`} onClick={handleTableClick}>
            {table}
          </Menu.Item>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown p="16px">
        {CUSTOM_FIELD_TYPE_OPTIONS.map(({ type, label, Icon }) => (
          <Menu.Item
            id={`${rootId}-type-${type}`}
            key={type}
            onClick={() => onSelectType(type)}
            leftSection={<Icon color="#ADB5BD" fontSize="16px" />}
          >
            {label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

export const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
    <g clipPath="url(#clip0_6109_22908)">
      <path
        d="M1.39703 3.72935C1.66849 2.57209 2.57209 1.66849 3.72935 1.39703C4.56511 1.20099 5.43489 1.20099 6.27065 1.39703C7.42791 1.66849 8.33151 2.57209 8.60297 3.72935C8.79901 4.56511 8.79901 5.43489 8.60297 6.27065C8.33151 7.42791 7.42791 8.33151 6.27065 8.60297C5.43489 8.79901 4.56511 8.79901 3.72935 8.60297C2.57209 8.33151 1.66849 7.42791 1.39703 6.27065C1.20099 5.43489 1.20099 4.56511 1.39703 3.72935Z"
        stroke="#DF4141"
      />
      <path d="M5 6.45964V4.79297" stroke="#DF4141" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="4.99967" cy="3.7487" r="0.416667" fill="#DF4141" />
    </g>
    <defs>
      <clipPath id="clip0_6109_22908">
        <rect width="10" height="10" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const useStyles = createStyles(theme => ({
  categoryName: {
    border: '1px solid #CED4DA',
    borderRadius: '4px',
    cursor: 'pointer'
  }
}));

export const Fields = observer(
  ({
    data,
    activeFundChildValue,
    onEnableValue,
    openUpdateCustomTableDrawer,
    category,
    isStructured,
    error,
    handleDeleteCustomField,
    selectedColumn,
    setSelectedColumnId,
    onClearFundValues,
    onSelectColumn,
    customFieldType,
    isExtrationOptionShown,
    handleCreateCustomField,
    setCustomFieldType,
    setOpenCustomFieldTypeList,
    inputRef,
    handleChange,
    newField,
    isLookup,
    selectedTableName,
    setSelectedTableName,
    onFullSelectionStructuredCells,
    sheetId,
    categoryName,
    tableName,
    isManual,
    onSelectCustomTable,
    isLockedPeriod,
    isLoadingCreateCustomField
  }) => {
    const rootId = `grid-table-fields-${idCreator(categoryName)}-${idCreator(tableName)}-${idCreator(sheetId)}`;
    const [action, setAction] = useState({});
    const [field, setField] = useState({});
    const [selectedTable, setSelectedTable] = useState();
    const { reportType } = extractionStore;

    const { classes } = useStyles();

    const isCustom = reportType === REPORT_TYPES.CUSTOM;

    const availableTypeOptions = useMemo(() => {
      if (!isCustom) return CUSTOM_FIELD_TYPE_OPTIONS;
      return CUSTOM_FIELD_TYPE_OPTIONS.filter(
        ({ type }) => type !== variableTypes.INTEGER && type !== variableTypes.BOOLEAN
      );
    }, [isCustom]);

    const customFields = useMemo(() => {
      return computeCustomFields(data, category);
    }, [data, category]);

    // Integrate prompts management hook
    const {
      prompts,
      getPromptValue,
      onCreatePrompt,
      onDeletePrompt,
      onUpdatePrompt,
      createLoading,
      updateLoading,
      deleteLoading
    } = useFieldPrompts(sheetId, categoryName, tableName, isLookup, field, setField);

    const requiredFields = useMemo(() => {
      return getRequiredFields(category);
    }, [category?.tables]);

    // Integrate field selection hook
    const { selectedExtract, isAllFieldsSelected, onToggleFields, onSelectExtract } =
      useFieldSelection(requiredFields, customFields, isLookup, tableName, category, isCustom);

    // Match original: refetch prompts when category/tables change
    useEffect(() => {
      if (isLookup) {
        prompts.refetch();
        return;
      }

      prompts.refetch();
    }, [category?.name, category?.tables, isLookup]);

    const isReMapBtnVisible = useMemo(() => {
      return shouldShowRemapButton(requiredFields, customFields);
    }, [requiredFields, customFields]);

    const onSelectType = type => {
      setOpenCustomFieldTypeList(false);

      // to prevent random outside click and submit form
      setTimeout(() => {
        setCustomFieldType(type);
      }, 200);
    };

    const handleCloseCustomFieldInputPopover = () => {
      setSelectedColumnId(null);
      setSelectedTableName(null);
      setCustomFieldType(null);
      handleChange({ target: { value: null } });
    };

    useHotkeys(
      'escape',
      () => {
        handleCloseCustomFieldInputPopover();
      },
      {
        enableOnFormTags: ['INPUT']
      }
    );

    const handleEnableValue = (tableId, val) => {
      onEnableValue(tableId, val);
      onSelectColumn(val);
    };

    // Extracted event handlers
    const {
      handleFieldClick,
      handleRequiredFieldClick,
      handleCustomFieldClick,
      handleClearColumn,
      handleDeleteField
    } = useFieldActions(
      isExtrationOptionShown,
      onSelectExtract,
      handleEnableValue,
      onSelectColumn,
      onClearFundValues,
      handleDeleteCustomField,
      setAction,
      selectedColumn
    );

    const handleCloseButtonClick = () => {
      onSelectType(null);
    };

    const getFieldTypeIcon = useCallback(value => {
      if (value?.type?.includes(variableTypes.STRING) && !value?.format)
        return (
          <TbTypography
            fontSize="12px"
            style={{
              minWidth: '12px'
            }}
          />
        );

      if (value?.type?.includes(variableTypes.INTEGER) && !value?.format)
        return <Tb123 fontSize="12px" style={{ minWidth: '12px' }} />;

      if (value?.type?.includes(variableTypes.NUMBER) && !value?.format)
        return (
          <GoNumber
            fontSize="12px"
            style={{
              minWidth: '12px'
            }}
          />
        );

      if (
        value?.type?.includes(variableTypes.DATE_TIME) ||
        value?.format === variableTypes.DATE_TIME_DASH
      )
        return (
          <TbCalendar
            fontSize="12px"
            style={{
              minWidth: '12px'
            }}
          />
        );

      if (value?.type?.includes(variableTypes.BOOLEAN))
        return (
          <TbToggleLeft
            fontSize="12px"
            style={{
              minWidth: '12px'
            }}
          />
        );

      return <></>;
    }, []);

    const getTypeIcon = type => {
      const option = CUSTOM_FIELD_TYPE_OPTIONS.find(opt => opt.type === type);
      if (!option) return undefined;
      const { Icon } = option;
      return <Icon color="#ADB5BD" fontSize="16px" />;
    };

    if (isLookup || isCustom)
      return (
        <>
          <Box id={rootId}>
            <Flex id={`${rootId}-header`} align="center" mb="16" justify="space-between">
              <Text id={`${rootId}-category-name`} fz="14px" fw="600">
                {category?.name}
              </Text>

              <Flex id={`${rootId}-header-actions`} align="center" gap="10px">
                {isExtrationOptionShown && (
                  <>
                    <Flex id={`${rootId}-select-all-wrap`} align="center" gap="4px">
                      <Checkbox
                        id={`${rootId}-select-all-checkbox`}
                        checked={isAllFieldsSelected}
                        onChange={onToggleFields}
                        size="xs"
                      />
                      <Text id={`${rootId}-select-all-text`} fz="12px">
                        {isAllFieldsSelected ? 'Unselect' : 'Select'} all
                      </Text>
                    </Flex>
                  </>
                )}
                {isManual && isStructured && isReMapBtnVisible && (
                  <Button
                    id={`${rootId}-remap-button`}
                    variant="subtle"
                    size="xs"
                    onClick={onFullSelectionStructuredCells}
                    h="25px"
                  >
                    Re-map fields
                  </Button>
                )}
              </Flex>
            </Flex>

            {category?.tables?.map((table, tIdx) => (
              <Box id={`${rootId}-table-wrap-${table.id}`} pb="16px" key={table.id}>
                {tIdx > 0 && <h4 id={`${rootId}-table-spacer-${table.id}`}></h4>}

                <Flex id={`${rootId}-table-header-${table.id}`} align="center" gap="10px" mb="6px">
                  <Flex id={`${rootId}-table-title-wrap-${table.id}`} align="center" gap="4px">
                    <Text id={`${rootId}-table-title-${table.id}`} fz="14px" fw="600">
                      {table.name}
                    </Text>
                    {isCustom && (
                      <ActionIcon
                        id={`${rootId}-edit-table-${table.id}`}
                        onClick={() => openUpdateCustomTableDrawer(table)}
                        variant="subtle"
                        color="gray"
                        size="xs"
                      >
                        <TbPencil />
                      </ActionIcon>
                    )}
                    {(table.is_custom || isCustom) && (
                      <ActionIcon
                        id={`${rootId}-delete-table-${table.id}`}
                        onClick={() => onSelectCustomTable(table)}
                        variant="subtle"
                        color="gray"
                        size="xs"
                      >
                        <TbTrash />
                      </ActionIcon>
                    )}
                  </Flex>
                  {!table.is_custom && !isCustom && (
                    <Tooltip
                      maw="350px"
                      multiline
                      label={
                        <>
                          The required fields are used in data modeling stage in order to generate
                          report.
                        </>
                      }
                    >
                      <Text fz="14px">Required Fields</Text>
                    </Tooltip>
                  )}
                </Flex>

                <Flex align="center" wrap="wrap" gap="6px">
                  {table.columns?.map(item => {
                    const fieldItem = item.table ? item : { ...item, table: table.name };
                    const isActive =
                      activeFundChildValue?.id === item.id &&
                      (table?.name === selectedTable ||
                        table?.id === activeFundChildValue?.foundChildId);
                    const selectedItem = !isExtrationOptionShown
                      ? isActive
                      : isFieldSelected(selectedExtract, item, table.name);
                    return (
                      <Menu shadow="md" key={item.uuid || item.id}>
                        <FieldMenu
                          item={fieldItem}
                          selectedItem={selectedItem}
                          error={error}
                          classes={classes}
                          isLockedPeriod={isLockedPeriod}
                          isManual={isManual}
                          getFieldTypeIcon={getFieldTypeIcon}
                          getPromptValue={getPromptValue}
                          onCreatePrompt={onCreatePrompt}
                          onUpdatePrompt={onUpdatePrompt}
                          onDeletePrompt={onDeletePrompt}
                          isLoading={createLoading || updateLoading}
                          isDeleteLoading={deleteLoading}
                          onItemClick={handleFieldClick(item, table, selectedItem)}
                          onClearColumn={it => handleClearColumn(it)()}
                          showDelete={item.is_custom || isCustom}
                          onDeleteField={() => handleDeleteField(item)}
                          onOpenPrompt={setField}
                        />
                      </Menu>
                    );
                  })}

                  {!!customFieldType && selectedTableName === table.name && (
                    <>
                      <Flex
                        data-testid="submit-form"
                        onSubmit={e => handleCreateCustomField(e, table?.id)}
                        component="form"
                        align="center"
                        gap="16px"
                      >
                        <Input
                          id={`${rootId}-create-input-${table.id}`}
                          leftSection={
                            <Box
                              sx={() => ({
                                pointerEvents: 'auto'
                              })}
                            >
                              <Menu
                                id={`${rootId}-create-type-menu-${table.id}`}
                                shadow="md"
                                width={200}
                              >
                                <Menu.Target>
                                  <UnstyledButton
                                    id={`${rootId}-create-type-menu-target-${table.id}`}
                                    size="xs"
                                    pt={4}
                                  >
                                    {getTypeIcon(customFieldType)}
                                  </UnstyledButton>
                                </Menu.Target>

                                <Menu.Dropdown p="16px">
                                  {availableTypeOptions.map(({ type, label, Icon }) => (
                                    <Menu.Item
                                      id={`${rootId}-create-type-option-${table.id}-${type}`}
                                      key={type}
                                      onClick={() => {
                                        onSelectType(type);
                                        setSelectedTableName(table.name);
                                      }}
                                      leftSection={<Icon color="#ADB5BD" fontSize="16px" />}
                                    >
                                      {label}
                                    </Menu.Item>
                                  ))}
                                </Menu.Dropdown>
                              </Menu>
                            </Box>
                          }
                          size="xs"
                          value={newField}
                          onChange={handleChange}
                          ref={inputRef}
                          inputSize="xs"
                        />
                        <Flex id={`${rootId}-create-actions-${table.id}`} gap="8px" align="center">
                          <Tooltip
                            label="Creating custom field..."
                            disabled={!isLoadingCreateCustomField}
                          >
                            <ActionIcon
                              id={`${rootId}-create-submit-inline-${table.id}`}
                              variant="white"
                              color="gray"
                              size="xs"
                              type="submit"
                              disabled={isLoadingCreateCustomField}
                            >
                              <TbCheck fontSize="16px" />
                            </ActionIcon>
                          </Tooltip>
                          <CloseButton
                            id={`${rootId}-create-cancel-inline-${table.id}`}
                            onClick={handleCloseButtonClick}
                            variant="white"
                            color="gray"
                            size="xs"
                          />
                        </Flex>
                      </Flex>
                    </>
                  )}
                </Flex>
                {isCustom && (
                  <>
                    <Menu id={`${rootId}-add-required-menu-${table.id}`} shadow="md" width={200}>
                      {!isLockedPeriod && (
                        <Menu.Target>
                          <Button
                            id={`${rootId}-add-required-button-${table.id}`}
                            variant="subtle"
                            data-testid="add-custom-field-btn"
                            leftSection={<FiPlus />}
                            rightSection={<BiChevronDown />}
                            size="xs"
                            mt="8px"
                            h="27px"
                          >
                            Add Required Field
                          </Button>
                        </Menu.Target>
                      )}
                      <Menu.Dropdown p="16px">
                        {availableTypeOptions.map(({ type, label, Icon }) => (
                          <Menu.Item
                            id={`${rootId}-add-required-option-${table.id}-${type}`}
                            key={type}
                            onClick={() => {
                              onSelectType(type);
                              setSelectedTableName(table.name);
                            }}
                            leftSection={<Icon color="#ADB5BD" fontSize="16px" />}
                          >
                            {label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              </Box>
            ))}

            {!isCustom && (
              <>
                <Box
                  id={`${rootId}-custom-fields-intro`}
                  display="flex"
                  mt="16"
                  mb="8"
                  pt="16px"
                  align="center"
                  sx={(theme, u) => ({
                    borderTop: '1px solid',
                    borderColor: theme.colors.gray[1]
                  })}
                >
                  <Tooltip
                    id={`${rootId}-custom-fields-tooltip`}
                    label={
                      <>
                        The custom fields are only used in extraction and <br />
                        transformation stage in order to prepare data. They are not
                        <br />
                        used in data modeling stage to generate report.
                      </>
                    }
                  >
                    <Text id={`${rootId}-custom-fields-title`} fz="14px" fw={600}>
                      Custom Fields
                    </Text>
                  </Tooltip>
                </Box>
                <Menu id={`${rootId}-add-custom-menu-top`} shadow="md" width={200}>
                  {!isLockedPeriod && !isCustom && (
                    <Menu.Target>
                      <Button
                        id={`${rootId}-add-custom-button-top`}
                        variant="outline"
                        data-testid="add-custom-field-btn"
                        leftSection={<FiPlus />}
                        size="xs"
                        h="27px"
                      >
                        Add Custom Field
                      </Button>
                    </Menu.Target>
                  )}
                  <Menu.Dropdown p="16px">
                    {category?.tables?.map((tble, idx) => (
                      <MenuTableItem
                        idPrefix={`${rootId}-menu-table-${idCreator(tble.name)}`}
                        key={idx}
                        table={tble.name}
                        onSelectTable={() => setSelectedTableName(tble.name)}
                        onSelectType={onSelectType}
                      />
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </>
            )}
          </Box>
          <ConfirmModal
            id={`${rootId}-confirm-modal-custom`}
            isOpen={!!action.title}
            title={action.title}
            description={action.description}
            onClose={() => setAction({})}
            onConfirm={action.onConfirm}
            colorSchema={action.colorSchema}
            confirmButtonTitle={action.confirmButtonTitle}
          />
        </>
      );

    return (
      <>
        <Box id={`${rootId}-legacy-root`}>
          <Flex id={`${rootId}-legacy-header`} align="center" justify="space-between">
            <Text id={`${rootId}-legacy-category-name`} fz="14px" fw="600">
              {category?.name}
            </Text>

            <Flex id={`${rootId}-legacy-header-actions`} align="center" gap="10px">
              {isExtrationOptionShown && (
                <>
                  <Flex id={`${rootId}-legacy-select-all-wrap`} align="center" gap="4px">
                    <Checkbox
                      id={`${rootId}-select-all-checkbox-legacy`}
                      checked={isAllFieldsSelected}
                      onChange={onToggleFields}
                      size="xs"
                    />
                    <Text id={`${rootId}-legacy-select-all-text`} fz="12px">
                      {isAllFieldsSelected ? 'Unselect' : 'Select'} all
                    </Text>
                  </Flex>
                </>
              )}

              {isManual && isStructured && isReMapBtnVisible && !isLockedPeriod && (
                <Button
                  id={`${rootId}-remap-button-legacy`}
                  variant="subtle"
                  size="xs"
                  onClick={onFullSelectionStructuredCells}
                  h="25px"
                >
                  Re-map fields
                </Button>
              )}
            </Flex>
          </Flex>

          <Flex id={`${rootId}-required-fields-wrap-legacy`} mt="16" mb="8" align="center">
            <Tooltip
              maw="350px"
              multiline
              label="The required fields are used in data modeling stage in order to generate report."
            >
              <Text id={`${rootId}-required-fields-title`} fz="14px" fw={600}>
                Required Fields
              </Text>
            </Tooltip>
          </Flex>

          <Flex align="center" wrap="wrap" gap="6px">
            {requiredFields?.map(item => {
              const selectedItem = !isExtrationOptionShown
                ? selectedColumn?.id === item.id
                : !!selectedExtract.find(value => value.id === item.id);
              return (
                <Menu shadow="md" key={item.name}>
                  <FieldMenu
                    item={item}
                    selectedItem={selectedItem}
                    error={error}
                    classes={classes}
                    isLockedPeriod={isLockedPeriod}
                    isManual={isManual}
                    getFieldTypeIcon={getFieldTypeIcon}
                    getPromptValue={getPromptValue}
                    onCreatePrompt={onCreatePrompt}
                    onUpdatePrompt={onUpdatePrompt}
                    onDeletePrompt={onDeletePrompt}
                    isLoading={createLoading || updateLoading}
                    isDeleteLoading={deleteLoading}
                    onItemClick={handleRequiredFieldClick(item)}
                    onClearColumn={it => handleClearColumn(it)()}
                    tableName={item.table ?? tableName}
                    onOpenPrompt={setField}
                  />
                </Menu>
              );
            })}
          </Flex>

          <Box
            id={`${rootId}-custom-fields-wrap-legacy`}
            display="flex"
            mt="16"
            mb="8"
            pt="16px"
            align="center"
            sx={(theme, u) => ({
              borderTop: '1px solid',
              borderColor: theme.colors.gray[1]
            })}
          >
            <Tooltip
              id={`${rootId}-custom-fields-tooltip-legacy`}
              label={
                <>
                  The custom fields are only used in extraction and <br />
                  transformation stage in order to prepare data. They are not
                  <br />
                  used in data modeling stage to generate report.
                </>
              }
            >
              <Text id={`${rootId}-custom-fields-title-legacy`} fz="14px" fw={600}>
                Custom Fields
              </Text>
            </Tooltip>
          </Box>

          <Flex
            id={`${rootId}-custom-fields-list-wrap-legacy`}
            pb="16px"
            mb="16px"
            sx={(theme, u) => ({
              borderBottom: '1px solid',
              borderColor: theme.colors.gray[1]
            })}
            align="center"
            wrap="wrap"
            gap="6px"
          >
            {!!customFieldType && (
              <Flex
                id={`${rootId}-create-form-legacy`}
                data-testid="submit-form"
                onSubmit={handleCreateCustomField}
                className="input-container"
                component="form"
                align="center"
                gap="16px"
              >
                <Input
                  id={`${rootId}-create-input-legacy`}
                  leftSection={getTypeIcon(customFieldType)}
                  size="xs"
                  value={newField}
                  onChange={handleChange}
                  ref={inputRef}
                  inputSize="xs"
                />
                <Flex id={`${rootId}-create-actions-legacy`} gap="8px" align="center">
                  <Tooltip
                    id={`${rootId}-create-submit-tooltip-legacy`}
                    label="Creating custom field..."
                    disabled={!isLoadingCreateCustomField}
                  >
                    <ActionIcon
                      id={`${rootId}-create-submit`}
                      variant="white"
                      color="gray"
                      size="xs"
                      type="submit"
                      disabled={isLoadingCreateCustomField}
                    >
                      <TbCheck fontSize="16px" />
                    </ActionIcon>
                  </Tooltip>
                  <CloseButton
                    id={`${rootId}-create-cancel`}
                    onClick={() => {
                      onSelectType(null);
                    }}
                    variant="white"
                    color="gray"
                    size="xs"
                  />
                </Flex>
              </Flex>
            )}

            {customFields?.map(item => {
              const selectedItem = !isExtrationOptionShown
                ? selectedColumn?.id === item.id
                : !!selectedExtract.find(value => value.id === item.id);
              return (
                <Menu shadow="md" key={item.name}>
                  <FieldMenu
                    item={item}
                    selectedItem={selectedItem}
                    error={error}
                    classes={classes}
                    isLockedPeriod={isLockedPeriod}
                    isManual={isManual}
                    getFieldTypeIcon={getFieldTypeIcon}
                    getPromptValue={getPromptValue}
                    onCreatePrompt={onCreatePrompt}
                    onUpdatePrompt={onUpdatePrompt}
                    onDeletePrompt={onDeletePrompt}
                    isLoading={createLoading || updateLoading}
                    isDeleteLoading={deleteLoading}
                    onItemClick={handleCustomFieldClick(item)}
                    onClearColumn={it => handleClearColumn(it)()}
                    showDelete={true}
                    onDeleteField={() => handleDeleteField(item)}
                    tableName={item.table ?? tableName}
                    onOpenPrompt={setField}
                  />
                </Menu>
              );
            })}

            <Menu id={`${rootId}-add-custom-menu-bottom`} shadow="md" width={200}>
              {!isLockedPeriod && (
                <Menu.Target>
                  <Button
                    id={`${rootId}-add-custom-button-bottom`}
                    variant="outline"
                    data-testid="add-custom-field-btn"
                    leftSection={<FiPlus />}
                    size="xs"
                    h="27px"
                  >
                    Add Custom Field
                  </Button>
                </Menu.Target>
              )}
              <Menu.Dropdown p="16px">
                {availableTypeOptions.map(({ type, label, Icon }) => (
                  <Menu.Item
                    id={`${rootId}-add-custom-option-${type}`}
                    key={type}
                    onClick={() => onSelectType(type)}
                    leftSection={<Icon color="#ADB5BD" fontSize="16px" />}
                  >
                    {label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Box>
        <ConfirmModal
          isOpen={!!action.title}
          title={action.title}
          description={action.description}
          onClose={() => setAction({})}
          onConfirm={action.onConfirm}
          colorSchema={action.colorSchema}
          confirmButtonTitle={action.confirmButtonTitle}
        />
      </>
    );
  }
);
